'use client';

import { useScrollReveal } from './useScrollReveal';
import { COUNTRY_LIST } from '@/lib/constants';
import { getSlugForCountry, uiFb } from '@/lib/i18n';
import { getCountrySlugFromCode } from '@/lib/long-weekends';
import type { CountryCode } from '@/lib/types';
import { Flag } from '@/components/ui/Flag';

/**
 * Maps country codes to their best-time-to-visit slug (if one exists).
 * Derived from BEST_TIME_PAGES in best-time-pages.ts.
 */
const BEST_TIME_SLUGS: Partial<Record<CountryCode, string>> = {
  TH: 'best-time-to-visit-thailand',
  ID: 'best-time-to-visit-indonesia',
  VN: 'best-time-to-visit-vietnam',
  MN: 'best-time-to-visit-mongolia',
  ET: 'best-time-to-visit-ethiopia',
  IN: 'best-time-to-visit-india',
  MY: 'best-time-to-visit-malaysia',
  PH: 'best-time-to-visit-philippines',
  SG: 'best-time-to-visit-singapore',
  LA: 'best-time-to-visit-laos',
  MM: 'best-time-to-visit-myanmar',
};

/**
 * Maps country codes to their festival-index slug (if one exists).
 * Derived from FESTIVAL_INDEX_PAGES in festival-pages.ts.
 */
const FESTIVAL_SLUGS: Partial<Record<CountryCode, string>> = {
  TH: 'thailand-festivals',
  VN: 'vietnam-festivals',
  ID: 'indonesia-festivals',
  IN: 'india-festivals',
  MY: 'malaysia-festivals',
  SG: 'singapore-festivals',
  PH: 'philippines-festivals',
};

interface LandingCountryDirectoryProps {
  lang?: string;
}

export function LandingCountryDirectory({ lang = 'en' }: LandingCountryDirectoryProps) {
  const { ref, revealed } = useScrollReveal();

  return (
    <section
      id="countries"
      ref={ref}
      className="px-4 py-8 md:px-8 md:py-12 lg:px-16 xl:px-20"
    >
      <div className="mx-auto max-w-5xl">
        <p
          className={`scroll-reveal ${revealed ? 'revealed' : ''} mb-6 text-center text-sm font-semibold uppercase tracking-widest text-muted`}
        >
          {uiFb('ui.explore_by_country', 'Explore by country', lang)}
        </p>

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
          {COUNTRY_LIST.map((country, i) => {
            const slug = getSlugForCountry(country.code as CountryCode, lang)
              || getSlugForCountry(country.code as CountryCode, 'en');
            const lwSlug = getCountrySlugFromCode(country.code as CountryCode);
            const btSlug = BEST_TIME_SLUGS[country.code as CountryCode];
            const festSlug = FESTIVAL_SLUGS[country.code as CountryCode];

            return (
              <div
                key={country.code}
                className={`scroll-reveal ${revealed ? 'revealed' : ''} delay-${Math.min((i + 1) * 50, 500)} rounded-xl border border-border-subtle bg-surface-card p-3 transition-all hover:border-brand/40 hover:shadow-md`}
              >
                {/* Country name + flag -> country holiday page */}
                <a
                  href={slug ? `/${slug}` : '#'}
                  className="flex items-center gap-2 font-heading text-sm font-bold text-text-primary hover:text-brand"
                >
                  <Flag code={country.code} size={20} />
                  {country.label}
                </a>

                {/* Sub-links to content verticals */}
                <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1">
                  {lwSlug && (
                    <a
                      href={`/${lwSlug}`}
                      className="text-label text-text-secondary transition-colors hover:text-brand"
                    >
                      {uiFb('ui.long_weekends', 'Long Weekends', lang)}
                    </a>
                  )}
                  {festSlug && (
                    <a
                      href={`/${festSlug}`}
                      className="text-label text-text-secondary transition-colors hover:text-brand"
                    >
                      {uiFb('ui.festivals', 'Festivals', lang)}
                    </a>
                  )}
                  {btSlug && (
                    <a
                      href={`/${btSlug}`}
                      className="text-label text-text-secondary transition-colors hover:text-brand"
                    >
                      {uiFb('ui.best_time', 'Best Time', lang)}
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
